import React, { useMemo, useState, useEffect } from 'react';
import { EmailPreview } from 'react-scale/components/scalers/tasks/textcollection/WorkspaceComponents/EmailPreview';
import {
  Email,
  EmailEditKey,
} from 'react-scale/components/scalers/tasks/textcollection/WorkspaceComponents/EmailView';
import {
  AttachmentMetadataType,
  AudioAttachment,
  ChatAttachment,
  EmailAttachment,
  ImageAttachment,
  IFrameAttachment,
} from '@scale/shared/types/chat';
import { ChatAudioEditor } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioEditor';
import { ChatAudioTranscript } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioTranscript';
import { getAudioSource } from 'react-scale/lib/chat-api/multimodal';
import { useQuery } from 'react-query';

interface AttachmentViewerProps {
  attachments: ChatAttachment[];
  setAttachments?: any;
  useEditor?: boolean | undefined;
  canEdit?: boolean | undefined;
  setAllViewed?: (viewed: boolean) => void;
}

const ENFORCE_VIEWING_TYPES = [AttachmentMetadataType.Email];

export const AttachmentViewer: React.FC<AttachmentViewerProps> = props => {
  const { attachments, useEditor = false, setAllViewed, setAttachments, canEdit } = props;
  const [totalAttachments, setTotalAttachments] = useState(0);
  const [viewedAttachments, setViewedAttachments] = useState<string[]>([]);

  const handleEmailEdit = (
    newValue: string,
    editKey: EmailEditKey,
    attachmentIndex: number = 0,
    metadataIndex: number = 0,
  ) => {
    setAttachments(
      attachments.map((a, i) => {
        if (i === attachmentIndex) {
          if (metadataIndex >= 0) {
            if (editKey === EmailEditKey.Body) {
              (a as EmailAttachment).metadata[metadataIndex].messages[0].body = newValue;
            } else if (editKey === EmailEditKey.Subject) {
              (a as EmailAttachment).metadata[metadataIndex].messages[0].subject = newValue;
              (a as EmailAttachment).metadata[metadataIndex].subject = newValue;
            }
          } else if (editKey === EmailEditKey.Body) {
            (a as EmailAttachment).metadata.messages[0].body = newValue;
          } else if (editKey === EmailEditKey.Subject) {
            (a as EmailAttachment).metadata.messages[0].subject = newValue;
            (a as EmailAttachment).metadata.subject = newValue;
          }
          return a;
        } else {
          return a;
        }
      }),
    );
  };

  const handleView = (key: string) => {
    if (viewedAttachments.includes(key)) return;
    setViewedAttachments(prev => [...prev, key]);
  };

  useEffect(() => {
    if (totalAttachments === viewedAttachments.length && setAllViewed) {
      setAllViewed(true);
    }
  }, [totalAttachments, viewedAttachments, setAllViewed]);

  const renderEmail = (
    attachment: EmailAttachment,
    attachmentIndex: number,
  ): JSX.Element | JSX.Element[] => {
    const { metadata, caption } = attachment;
    const emails = metadata as Email[] | Email;
    let emailComponent;
    if (Array.isArray(emails)) {
      emailComponent = emails.map((email: Email, metadataIndex: number) => {
        return useEditor ? (
          <EmailPreview
            key={`${email.threadId}-${attachmentIndex}-${metadataIndex}`}
            email={email}
            useEditor={useEditor}
            attachmentIndex={attachmentIndex}
            metadataIndex={metadataIndex}
            canEdit={canEdit}
            handleEmailEdit={handleEmailEdit}
            onView={handleView}
          />
        ) : (
          <EmailPreview
            key={email.threadId}
            email={email}
            viewed={viewedAttachments.includes(email.threadId)}
            onView={handleView}
          />
        );
      });
    } else {
      emailComponent = useEditor ? (
        <EmailPreview
          key={`${emails.threadId}-${attachmentIndex}`}
          email={emails}
          useEditor={useEditor}
          attachmentIndex={attachmentIndex}
          metadataIndex={-1}
          canEdit={canEdit}
          handleEmailEdit={handleEmailEdit}
          onView={handleView}
        />
      ) : (
        <EmailPreview
          email={emails}
          viewed={viewedAttachments.includes(emails.threadId)}
          onView={handleView}
        />
      );
    }
    return caption ? (
      <>
        {emailComponent}
        <p>{caption}</p>
      </>
    ) : (
      emailComponent
    );
  };

  const { data: currentAudioBlob } = useQuery(
    ['getAudioBlob', attachments?.[0] ?? ''],
    async (): Promise<Blob | null> => {
      const maybeAudioAttachment = attachments?.[0];
      if (!maybeAudioAttachment) {
        return null;
      }
      if (
        maybeAudioAttachment.attachmentType === AttachmentMetadataType.Audio &&
        maybeAudioAttachment.url
      ) {
        const { source } = await getAudioSource({ audioUrl: maybeAudioAttachment.url });

        const data = await fetch(source);

        if (!data.ok) {
          throw new Error('Failed to fetch audio blob');
        }

        return await data.blob();
      }
      return null;
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const renderAudio = (attachment: AudioAttachment): JSX.Element | null => {
    const { url } = attachment;
    return url ? (
      <div>
        {currentAudioBlob ? (
          <div>
            <ChatAudioEditor readonly originalBlob={currentAudioBlob} />
            <ChatAudioTranscript audioUrl={url} userGeneratedAudio={false} />
          </div>
        ) : (
          <audio controls key={url}>
            <source src={url} type="audio/wav" />
          </audio>
        )}
      </div>
    ) : null;
  };

  const renderImage = (attachment: ImageAttachment): JSX.Element | null => {
    const { url, caption } = attachment;
    return url ? (
      <figure>
        <img src={url} />
        <figcaption>{caption}</figcaption>
      </figure>
    ) : null;
  };

  const renderIFrame = (attachment: IFrameAttachment): JSX.Element | null => {
    const { url, caption } = attachment;
    return url ? (
      <figure>
        <iframe src={url} className="w-full h-[500px]" sandbox="allow-same-origin allow-scripts" />
        <figcaption>{caption}</figcaption>
      </figure>
    ) : null;
  };

  const renderAttachment = (attachment: ChatAttachment, attachmentIndex: number) => {
    const { attachmentType } = attachment;
    switch (attachmentType) {
      case AttachmentMetadataType.Email:
        return renderEmail(attachment, attachmentIndex);
      case AttachmentMetadataType.Image:
        return renderImage(attachment);
      case AttachmentMetadataType.IFrame:
        return renderIFrame(attachment);
      case AttachmentMetadataType.Audio:
        return renderAudio(attachment);
      default:
        return null;
    }
  };

  const parsedAttachments = useMemo(() => {
    let totalAttachments = 0;
    const processedAttachments = attachments.map(attachment => {
      const { attachmentType } = attachment;

      if (attachmentType === AttachmentMetadataType.Email) {
        const { metadata } = attachment;
        let parsedMetadata = metadata;
        if (typeof metadata === 'string') {
          try {
            parsedMetadata = JSON.parse(metadata);
          } catch (e) {
            parsedMetadata = [];
          }
        }

        if (ENFORCE_VIEWING_TYPES.includes(AttachmentMetadataType.Email)) {
          if (Array.isArray(parsedMetadata)) {
            totalAttachments += parsedMetadata.length;
          } else {
            totalAttachments += 1;
          }
        }

        return {
          ...attachment,
          metadata: parsedMetadata,
        };
      } else {
        if (ENFORCE_VIEWING_TYPES.includes(attachmentType)) {
          totalAttachments += 1;
        }

        return attachment;
      }
    });

    setTotalAttachments(totalAttachments);

    return processedAttachments.flat();
  }, [attachments]);

  return (
    <div className="mt-16">
      {parsedAttachments.map((attachment, index) => {
        return (
          <div key={index} className="mt-2">
            {renderAttachment(attachment, index)}
          </div>
        );
      })}
    </div>
  );
};
